<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { computed, onMounted, reactive, watch } from "vue";
import BorderInput from "./BorderInput.vue";
import BorderType from "./BorderType.vue";
import Card from "./Card.vue";
import ColorInputField from "./ColorInputField.vue";
import CountInput from "./CountInput.vue";
import RichTextEditor from "@/components/text-editor/index.vue";

import { useI18n } from "vue-i18n";
import { defaultBorder, defaultPadding, defaultShadow } from "./../../util/default";
import { UITextMdNormal } from "@gohighlevel/ghl-ui";
import {
  getActiveGroup,
  getDeviceAware,
  isMobileDevice,
  setActiveGroupValue,
  setDeviceAware,
} from "@/composition/useDeviceAware";

const { t } = useI18n();
const store = useAppStore();

const props = defineProps({
  property: {
    type: Object,
    default() {
      return {
        label: "Text",
        tag: "header",
        type: "h1",
        placeholder: "header",
        weight: 400,
        bgColor: "FFFFFF",
        align: "left",
        shadow: defaultShadow,
        padding: defaultPadding,
        border: defaultBorder,
      };
    },
  },
});
const output = reactive({ ...props.property });

watch(
  () => props.property,
  (newVal) => {
    const keys = Object.keys(newVal);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      (output as any)[key] = (newVal as any)[key];
    }
  },
  {
    deep: true,
  }
);

const emits = defineEmits(["update:text"]);

const isMobile = computed(() => isMobileDevice((store as any).deviceMode));
const defaults = { padding: defaultPadding, border: defaultBorder, shadow: defaultShadow };

// Helpers to normalize hex values without '#'
const stripHash = (v: any): string => String(v ?? "").replace(/^#/, "");
const withFallback = (v: string, fb: string): string => (v && v.trim().length > 0 ? v : fb);

// Device-aware label
const labelValue = computed<string>(() =>
  String(getDeviceAware(output, "label", "mobileLabel", isMobile.value) ?? "")
);

// Values (device-aware)
const weightValue = computed<number>(() =>
  Number(getDeviceAware(output, "weight", "mobileWeight", isMobile.value) ?? 0)
);
const bgColorValue = computed<string>(() =>
  withFallback(
    stripHash(getDeviceAware(output, "bgColor", "mobileBgColor", isMobile.value)),
    "FFFFFF"
  )
);

const activePadding = computed<any>(() => getActiveGroup(output, "padding", isMobile.value));
const activeBorder = computed<any>(() => getActiveGroup(output, "border", isMobile.value));
const activeShadow = computed<any>(() => getActiveGroup(output, "shadow", isMobile.value));

const borderWidthValue = computed<number>(() => Number(activeBorder.value?.border ?? 0));
const borderColorValue = computed<string>(() =>
  withFallback(stripHash(activeBorder.value?.color), "000000")
);
const borderTypeValue = computed<string>(() => String(activeBorder.value?.type ?? "solid"));
const borderRadiusValue = computed<number>(() => Number(activeBorder.value?.radius ?? 0));

const paddingTopValue = computed<number>(() => Number(activePadding.value?.top ?? 0));
const paddingBottomValue = computed<number>(() => Number(activePadding.value?.bottom ?? 0));
const paddingLeftValue = computed<number>(() => Number(activePadding.value?.left ?? 0));
const paddingRightValue = computed<number>(() => Number(activePadding.value?.right ?? 0));

const shadowColorValue = computed<string>(() =>
  withFallback(stripHash(activeShadow.value?.color), "000000")
);
const shadowHorizontalValue = computed<number>(() => Number(activeShadow.value?.horizontal ?? 0));
const shadowVerticalValue = computed<number>(() => Number(activeShadow.value?.vertical ?? 0));
const shadowBlurValue = computed<number>(() => Number(activeShadow.value?.blur ?? 0));
const shadowSpreadValue = computed<number>(() => Number(activeShadow.value?.spread ?? 0));

const handleTextFieldStyling = (value, type) => {
  switch (type) {
    case "text":
      setDeviceAware(output, "label", "mobileLabel", value, isMobile.value);
      break;
    case "fontWeight":
      setDeviceAware(output, "weight", "mobileWeight", value, isMobile.value);
      break;
    case "background": {
      const safe = withFallback(stripHash(value), "FFFFFF");
      setDeviceAware(output, "bgColor", "mobileBgColor", safe, isMobile.value);
      break;
    }
    case "border":
      setActiveGroupValue(output, "border", "border", value, isMobile.value, defaults);
      break;
    case "borderColor": {
      const safe = withFallback(stripHash(value), "000000");
      setActiveGroupValue(output, "border", "color", safe, isMobile.value, defaults);
      break;
    }
    case "borderType":
      setActiveGroupValue(output, "border", "type", value, isMobile.value, defaults);
      break;
    case "cornerRadius":
      setActiveGroupValue(output, "border", "radius", value, isMobile.value, defaults);
      break;
    case "paddingTop":
      setActiveGroupValue(output, "padding", "top", value, isMobile.value, defaults);
      break;
    case "paddingBottom":
      setActiveGroupValue(output, "padding", "bottom", value, isMobile.value, defaults);
      break;
    case "paddingLeft":
      setActiveGroupValue(output, "padding", "left", value, isMobile.value, defaults);
      break;
    case "paddingRight":
      setActiveGroupValue(output, "padding", "right", value, isMobile.value, defaults);
      break;
    case "shadowColor": {
      const safe = withFallback(stripHash(value), "000000");
      setActiveGroupValue(output, "shadow", "color", safe, isMobile.value, defaults);
      break;
    }
    case "shadowHorizontal":
      setActiveGroupValue(output, "shadow", "horizontal", value, isMobile.value, defaults);
      break;
    case "shadowVertical":
      setActiveGroupValue(output, "shadow", "vertical", value, isMobile.value, defaults);
      break;
    case "shadowBlur":
      setActiveGroupValue(output, "shadow", "blur", value, isMobile.value, defaults);
      break;
    case "shadowSpread":
      setActiveGroupValue(output, "shadow", "spread", value, isMobile.value, defaults);
      break;
    default:
      break;
  }
  store.anyUnsavedChanges = true;
  emits("update:text", output);
};

onMounted(() => {
  if ("fontFamily" in output) {
    delete (output as any).fontFamily;
  }
  if ("fontSize" in output) {
    delete (output as any).fontSize;
  }
  if ("color" in output) {
    delete (output as any).color;
  }
});
</script>

<template>
  <RichTextEditor
    :model-value="labelValue"
    :is-submit-message="true"
    @update:model-value="(value) => handleTextFieldStyling(value, 'text')"
  ></RichTextEditor>
  <Card :header="t('common.textStyle')">
    <CountInput
      :label="t('common.weight')"
      :value="weightValue"
      :max="1800"
      @update:value="(value) => handleTextFieldStyling(value, 'fontWeight')"
    />
    <ColorInputField
      :label="t('common.background')"
      :color="bgColorValue"
      @update:color="(value) => handleTextFieldStyling(value, 'background')"
    />
    <BorderInput
      :border="borderWidthValue"
      :border-color="borderColorValue"
      @update:border="(value) => handleTextFieldStyling(value, 'border')"
      @update:color="(value) => handleTextFieldStyling(value, 'borderColor')"
    />
    <BorderType
      :border-type="borderTypeValue"
      @update:border-type="(value) => handleTextFieldStyling(value, 'borderType')"
    />
    <CountInput
      :label="t('common.cornerRadius')"
      :value="borderRadiusValue"
      @update:value="(value) => handleTextFieldStyling(value, 'cornerRadius')"
    />
    <div class="m-2"></div>
    <UITextMdNormal>{{ t("common.padding") }}</UITextMdNormal>
    <div class="my-2 grid grid-cols-2 gap-4">
      <CountInput
        :is-label-small="true"
        :label="t('common.top')"
        :value="paddingTopValue"
        @update:value="(value) => handleTextFieldStyling(value, 'paddingTop')"
      />
      <CountInput
        :is-label-small="true"
        :label="t('common.bottom')"
        :value="paddingBottomValue"
        @update:value="(value) => handleTextFieldStyling(value, 'paddingBottom')"
      />
    </div>
    <div class="my-2 grid grid-cols-2 gap-4">
      <CountInput
        :is-label-small="true"
        :label="t('common.left')"
        :value="paddingLeftValue"
        @update:value="(value) => handleTextFieldStyling(value, 'paddingLeft')"
      />
      <CountInput
        :is-label-small="true"
        :label="t('common.right')"
        :value="paddingRightValue"
        @update:value="(value) => handleTextFieldStyling(value, 'paddingRight')"
      />
    </div>
    <div class="m-2"></div>
    <UITextMdNormal>{{ t("common.shadow") }}</UITextMdNormal>
    <ColorInputField
      :label="t('common.color')"
      :color="shadowColorValue"
      @update:color="(value) => handleTextFieldStyling(value, 'shadowColor')"
    />
    <div class="my-2 grid grid-cols-2 gap-4">
      <CountInput
        :is-label-small="true"
        :label="t('common.horizontal')"
        :value="shadowHorizontalValue"
        @update:value="(value) => handleTextFieldStyling(value, 'shadowHorizontal')"
      />
      <CountInput
        :is-label-small="true"
        :label="t('common.vertical')"
        :value="shadowVerticalValue"
        @update:value="(value) => handleTextFieldStyling(value, 'shadowVertical')"
      />
    </div>
    <div class="my-2 grid grid-cols-2 gap-4">
      <CountInput
        :is-label-small="true"
        :label="t('common.blur')"
        :value="shadowBlurValue"
        @update:value="(value) => handleTextFieldStyling(value, 'shadowBlur')"
      />
      <CountInput
        :is-label-small="true"
        :label="t('common.spread')"
        :value="shadowSpreadValue"
        @update:value="(value) => handleTextFieldStyling(value, 'shadowSpread')"
      />
    </div>
  </Card>
</template>

<style>
.field-name-sidebar {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center !important;
  margin-top: 1rem;
}
</style>
